import React from "react";
import { RecoilRoot } from "recoil";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { StatusBar as ExpoStatusBar } from "expo-status-bar";
import { ApolloProvider } from "@apollo/client";
import "./configs/sentry.config";
import "./configs/mixpanel";
import client from "./configs/graphql.config";
import RootNavigation from "./navigations/RootNavigation";
import FirebaseManager from "./contexts/FirebaseManager";
import AuthManager from "./contexts/AuthManager";
import SafeAreaManager from "./contexts/SafeAreaManager";
import { EventProvider } from "react-native-outside-press";
import I18nGuard from "./components/I18nGuard";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import Constants from "expo-constants";
import DropdownManager from "./contexts/DropdownManager";
import SnackBarPortal from "./components/SnackbarZone";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import NetworkManager from "./contexts/NetworkManager";
import "./image-cache";
import { IntercomProvider } from "./contexts/IntercomProvider";
import * as SplashScreen from "expo-splash-screen";
import ScreenHeaderManager from "./contexts/ScreenHeaderManager";
import ProfileSwitcherManager from "./contexts/ProfileSwitcherManager";
import { ImageCropZoomPortal } from "./components/cropZoom/ImageCropZoomModal";
import { isStorybookMode, addStorybookToDevMenu } from "./sherlo";
import { KeyboardProvider } from "react-native-keyboard-controller";
import StepsManager from "./contexts/StepsManager";
import { theme } from "./theme";
import { ThemeProvider } from "./contexts/ThemeManager";
import { IntercomButton } from "./components/IntercomButton";

if (Platform.OS !== "web") {
    addStorybookToDevMenu();
}

/**
 * Prevent the splash screen from auto hiding,
 * hide is called in AuthManager AND in the App if we are in storybook mode
 */
// eslint-disable-next-line @typescript-eslint/no-floating-promises
SplashScreen.preventAutoHideAsync().then((result) => {
    // used to dbug some problem on some android phones
    // eslint-disable-next-line no-console
    console.log("preventAutoHideAsync", result);
});

// eslint-disable-next-line no-console
console.log(
    "App env",
    Constants.expoConfig?.extra?.appEnv,
    "Platform",
    Platform.OS,
    "Version",
    Platform.Version,
    "GitVersion",
    Constants.expoConfig?.extra?.gitVersion,
);

export default function App() {
    if (isStorybookMode || Constants.expoConfig?.extra?.displayStorybook) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const StorybookScreen = require("../.ondevice").default;
        return <StorybookScreen />;
    }

    return (
        <ThemeProvider value={theme}>
            <IntercomProvider
                {...(Platform.OS === "web"
                    ? {
                          appId: Constants.expoConfig?.extra?.intercomAppId,
                          autoBoot: true,
                          autoBootProps: {
                              hideDefaultLauncher: true, // always hide, we use a custom button on web
                          },
                      }
                    : {})}
            >
                <RecoilRoot>
                    <I18nGuard>
                        <ApolloProvider client={client}>
                            <FirebaseManager>
                                <ActionSheetProvider>
                                    <SafeAreaProvider>
                                        <EventProvider>
                                            <AuthManager>
                                                <ProfileSwitcherManager>
                                                    <SafeAreaManager>
                                                        <NetworkManager>
                                                            <GestureHandlerRootView
                                                                style={{
                                                                    flex: 1,
                                                                    backgroundColor:
                                                                        theme.colors.background,
                                                                }}
                                                            >
                                                                <KeyboardProvider>
                                                                    <DropdownManager>
                                                                        <ScreenHeaderManager>
                                                                            <StepsManager>
                                                                                <RootNavigation />
                                                                            </StepsManager>
                                                                        </ScreenHeaderManager>
                                                                    </DropdownManager>
                                                                </KeyboardProvider>
                                                            </GestureHandlerRootView>
                                                        </NetworkManager>
                                                    </SafeAreaManager>
                                                </ProfileSwitcherManager>
                                                <IntercomButton />
                                            </AuthManager>
                                        </EventProvider>
                                        <SnackBarPortal />
                                        <ImageCropZoomPortal />
                                    </SafeAreaProvider>
                                </ActionSheetProvider>
                            </FirebaseManager>
                            <ExpoStatusBar translucent style="dark" />
                        </ApolloProvider>
                    </I18nGuard>
                </RecoilRoot>
            </IntercomProvider>
        </ThemeProvider>
    );
}
